import React, { useEffect, useState } from "react";
import { manchiApi } from "@/lib/manchiApi";
import { Loader2, Newspaper, Check, CheckCheck, Eraser } from "lucide-react";
import { toast } from "sonner";

function monogram(name) {
  return name.replace(/[^a-zA-Z ]/g, "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

export default function ManchiSources() {
  const [catalog, setCatalog] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([manchiApi.get("/sources"), manchiApi.get("/config")])
      .then(([s, c]) => { setCatalog(s.data.catalog || []); setSources(c.data.sources || []); })
      .catch(() => toast.error("Couldn't load sources"))
      .finally(() => setLoading(false));
  }, []);

  const save = async (next) => {
    setSources(next);
    setSaving(true);
    try { await manchiApi.patch("/config", { sources: next }); }
    catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };

  const toggle = (slug) => {
    const has = sources.includes(slug);
    save(has ? sources.filter((s) => s !== slug) : [...sources, slug]);
  };

  if (loading) return <div className="text-center py-16 text-rose-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  return (
    <div className="space-y-6" data-testid="manchi-sources">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-rose-400 flex items-center gap-2">
            <Newspaper className="w-4 h-4" /> free reading list
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-rose-900 mt-1">Choose your papers.</h1>
          <p className="text-rose-500 text-sm mt-2">
            Pick the free Economics &amp; Finance outlets ManchiGirl reads from. {saving && <span className="text-pink-500">saving…</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => save(catalog.map((c) => c.slug))} data-testid="manchi-sources-selectall"
            className="text-xs font-semibold px-3 py-2 rounded-xl bg-pink-100 text-rose-600 hover:bg-pink-200 flex items-center gap-1.5">
            <CheckCheck className="w-4 h-4" /> Select all
          </button>
          <button onClick={() => save([])} data-testid="manchi-sources-clear"
            className="text-xs font-semibold px-3 py-2 rounded-xl bg-white border border-pink-200 text-rose-500 hover:bg-pink-50 flex items-center gap-1.5">
            <Eraser className="w-4 h-4" /> Clear
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {catalog.map((s) => {
          const sel = sources.includes(s.slug);
          return (
            <button
              key={s.slug}
              onClick={() => toggle(s.slug)}
              data-testid={`manchi-source-${s.slug}`}
              className={`relative text-left rounded-2xl border p-4 transition group ${
                sel ? "border-pink-400 bg-pink-50 shadow-md shadow-pink-200/50" : "border-pink-100 bg-white hover:border-pink-300"
              }`}
            >
              {sel && (
                <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-pink-500 text-white flex items-center justify-center">
                  <Check className="w-3 h-3" />
                </span>
              )}
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-black text-sm mb-3 shadow"
                style={{ background: s.tint, color: s.tint.toLowerCase() === "#fff1e5" ? "#33302e" : "#fff" }}>
                {monogram(s.name)}
              </div>
              <div className="font-bold text-rose-900 text-sm leading-tight">{s.name}</div>
              <div className="text-[11px] text-rose-400 mt-0.5 capitalize">{s.kind} · {s.domain}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
